/**
 * Calendar utility functions — all Luxon-based
 */
import { DateTime } from 'luxon'

function getLocale() {
  return (
    (window.FcaEventsConfig?.locale || document.documentElement.lang || 'en')
      .replace(/_/g, '-')
  )
}

/**
 * Returns the start-of-week DateTime for the given date.
 * weekStartDay: 'monday' | 'sunday'
 */
export function getWeekStart(date, weekStartDay = 'monday') {
  const dow = date.weekday // 1=Mon … 7=Sun (Luxon ISO weekday)
  if (weekStartDay === 'sunday') {
    const shift = dow === 7 ? 0 : dow
    return date.minus({ days: shift }).startOf('day')
  }
  return date.minus({ days: dow - 1 }).startOf('day')
}

/**
 * Build 7-day array for the week view.
 * Each entry: { date, dateString, isToday, shortLabel, dayNumber }
 */
export function buildWeekDays(currentDate, weekStartDay = 'monday') {
  const now    = DateTime.now()
  const start  = getWeekStart(currentDate, weekStartDay)
  const locale = getLocale()
  return Array.from({ length: 7 }, (_, i) => {
    const d = start.plus({ days: i })
    return {
      date:       d,
      dateString: d.toISODate(),
      isToday:    d.hasSame(now, 'day'),
      shortLabel: d.setLocale(locale).toFormat('ccc'),
      dayNumber:  d.day,
    }
  })
}

/**
 * Build 6-row × 7-col month grid.
 * Returns array of 6 weeks, each a 7-cell array:
 * { date, dateString, day, isToday, isCurrentMonth }
 */
export function buildMonthGrid(currentDate, weekStartDay = 'monday') {
  const now          = DateTime.now()
  const firstOfMonth = currentDate.startOf('month')
  const gridStart    = getWeekStart(firstOfMonth, weekStartDay)
  const cells        = []
  for (let i = 0; i < 42; i++) {
    const d = gridStart.plus({ days: i })
    cells.push({
      date:           d,
      dateString:     d.toISODate(),
      day:            d.day,
      isToday:        d.hasSame(now, 'day'),
      isCurrentMonth: d.month === currentDate.month,
    })
  }
  // Group into 6 weeks of 7
  return Array.from({ length: 6 }, (_, w) => cells.slice(w * 7, w * 7 + 7))
}

/**
 * Build time-slot rows for the week view.
 * interval: minutes per slot (default 60)
 * Returns: [{ value, hour, minute, label }]
 */
export function buildTimeSlots(startHour = 0, endHour = 23, interval = 60, timeFormat = '12h') {
  const cfg = window.FcaEventsConfig?.settings || {}
  const fmt = timeFormat || cfg.time_format || '12h'
  const slots = []
  for (let h = startHour; h <= endHour; h += interval / 60) {
    const hour   = Math.floor(h)
    const minute = Math.round((h - hour) * 60)
    let label
    if (fmt === '12h') {
      const ampm    = hour < 12 ? 'AM' : 'PM'
      const display = hour % 12 === 0 ? 12 : hour % 12
      label = `${display} ${ampm}`
    } else {
      label = String(hour).padStart(2, '0') + ':' + String(minute).padStart(2, '0')
    }
    slots.push({ value: `${hour}:${String(minute).padStart(2,'0')}`, hour, minute, label })
  }
  return slots
}

/**
 * Index events by their start date (date_local).
 * Returns: { 'YYYY-MM-DD': [event, …], … }
 */
export function indexEventsByDate(events) {
  const map = {}
  for (const ev of events) {
    const key = ev.date_local
    if (!key) continue
    if (!map[key]) map[key] = []
    map[key].push(ev)
  }
  return map
}

/**
 * Get events for a specific dateString from an indexed map.
 */
export function getEventsForDate(indexedEvents, dateString) {
  return indexedEvents[dateString] || []
}

/**
 * Compute pixel position for an event in the week timeline.
 * Returns { top, height } in pixels.
 *
 * API returns UTC datetimes in MySQL format ("YYYY-MM-DD HH:MM:SS").
 * Parse as UTC then convert to the event's own timezone so the hour
 * matches the local-time column the event sits in.
 */
export function getEventPosition(event, slotHeight = 50, startHour = 0) {
  const zone = event.timezone || 'UTC'

  // fromSQL handles "YYYY-MM-DD HH:MM:SS" (MySQL/space-separated) stored as UTC
  const parseUtcToLocal = (str) => {
    if (!str) return null
    const dt = DateTime.fromSQL(str, { zone: 'UTC' })
    return dt.isValid ? dt.setZone(zone) : null
  }

  const startDt = parseUtcToLocal(event.start || event.date)
  const endDt   = parseUtcToLocal(event.end_time || event.end)

  if (!startDt) return { top: 0, height: slotHeight }

  const startMins = (startDt.hour - startHour) * 60 + startDt.minute
  const endMins   = endDt
    ? (endDt.hour - startHour) * 60 + endDt.minute
    : startMins + 60

  return {
    top:    (startMins / 60) * slotHeight,
    height: Math.max(((endMins - startMins) / 60) * slotHeight, 22),
  }
}

/**
 * Format month + year header: "March 2026"
 */
export function formatMonthHeader(date) {
  return date.setLocale(getLocale()).toFormat('MMMM yyyy')
}

/**
 * Build the 12-month selector entries centered on currentDate.
 * Returns: [{ value: 'YYYY-MM', label: 'JAN', date: DateTime }]
 */
export function buildMonthSelector(currentDate, count = 12) {
  const locale = getLocale()
  // Show current year months
  const year   = currentDate.year
  return Array.from({ length: count }, (_, i) => {
    const d = DateTime.fromObject({ year, month: i + 1 })
    return {
      value: d.toFormat('yyyy-MM'),
      label: d.setLocale(locale).toFormat('MMMM'),
      date:  d,
    }
  })
}

/**
 * Check whether a date is today.
 */
export function isToday(date) {
  return date.toISODate() === DateTime.now().toISODate()
}

/**
 * Abbreviate weekday names for the calendar header row.
 * Returns array of 7 { key, short, full } objects starting from weekStartDay.
 */
export function buildWeekdayLabels(weekStartDay = 'monday') {
  const locale = getLocale()
  const labels = []
  const startIso = weekStartDay === 'sunday' ? 7 : 1 // Luxon weekday for Sun is 7
  for (let c = 0; c < 7; c++) {
    const isoDay = weekStartDay === 'sunday'
      ? ((startIso - 1 + c) % 7) + 1 // wrap Sun(7) around: 7,1,2,3,4,5,6
      : ((startIso - 1 + c) % 7) + 1
    const d = DateTime.local().set({ weekday: isoDay === 0 ? 7 : isoDay })
    labels.push({
      key:   isoDay,
      short: d.setLocale(locale).toFormat('ccc'),
      full:  d.setLocale(locale).toFormat('cccc'),
    })
  }
  return labels
}
